import React from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel,
  Grid, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import { customersApi } from "../../api/customers";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const PERIOD_LABELS = { days: "días", weeks: "semanas", months: "meses", years: "años" };
const PAYMENT_METHOD_LABELS = {
  cash: "Efectivo",
  bank_transfer: "Transferencia",
  card: "Tarjeta",
  digital_wallet: "Billetera virtual",
  check: "Cheque",
  other: "Otro",
};
const EMPTY_PAYMENT_CONTEXT = {
  mode: "administrative", systemActive: false, canRecord: false, methods: [], registers: [], note: "",
};

function todayDate() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function createOperationKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `membership-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function formatDateOnly(value) {
  if (!value) return "—";
  const [year, month, day] = String(value).split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

function formatMoney(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency", currency: currency || "ARS", maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function statusMeta(status) {
  if (status === "scheduled") return { label: "Programada", color: "info" };
  if (status === "expired") return { label: "Vencida", color: "default" };
  if (status === "cancelled") return { label: "Cancelada", color: "error" };
  return { label: "Activa", color: "success" };
}

function paymentMeta(payment) {
  if (payment?.mode === "erp") return { label: "Cobrado en gestión", color: "success" };
  if (payment?.mode === "administrative") return { label: "Cobro administrativo", color: "info" };
  if (payment?.status === "unpaid") return { label: "Cobro pendiente", color: "warning" };
  return null;
}

function firstUsableRegister(paymentContext, currency = "ARS") {
  return (paymentContext?.registers || []).find(
    (register) => register.available && register.currency === currency,
  ) || null;
}

function emptyForm() {
  return {
    itemId: "", startDate: todayDate(), periodValue: 1, periodUnit: "months",
    endDate: "", value: "", currency: "ARS", renewFromMembershipId: "",
    operationKey: createOperationKey(), recordPayment: false, cashRegisterId: "",
    paymentMethod: "cash", paymentReference: "",
  };
}

function renewalStart(membership) {
  const today = todayDate();
  if (!membership?.endDate || membership.endDate < today) return today;
  const date = new Date(`${membership.endDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

function daysBetween(fromDate, toDate) {
  const from = new Date(`${fromDate}T00:00:00.000Z`);
  const to = new Date(`${toDate}T00:00:00.000Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return Math.round((to.getTime() - from.getTime()) / 86400000);
}

function membershipOverview(memberships) {
  const today = todayDate();
  const rows = Array.isArray(memberships) ? memberships.filter((item) => item.status !== "cancelled") : [];
  const active = rows.filter((item) => item.status === "active").sort((a, b) => String(a.endDate).localeCompare(String(b.endDate)));
  const future = rows.filter((item) => item.status === "scheduled").sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
  const expiring = active.filter((item) => {
    const days = daysBetween(today, item.endDate);
    if (days === null || days < 0 || days > 7) return false;
    const continuation = new Date(`${item.endDate}T00:00:00.000Z`);
    continuation.setUTCDate(continuation.getUTCDate() + 1);
    const continuationLimit = continuation.toISOString().slice(0, 10);
    return !future.some((next) => next.item?.id === item.item?.id && String(next.startDate) <= continuationLimit);
  });
  const expired = rows.filter((item) => item.status === "expired").sort((a, b) => String(b.endDate).localeCompare(String(a.endDate)));

  if (expiring.length) return { status: "expiring", membership: expiring[0], attention: true };
  if (active.length) return { status: "active", membership: active[0], attention: false };
  if (future.length) return { status: "future", membership: future[0], attention: false };
  if (expired.length) return { status: "expired", membership: expired[0], attention: true };
  return { status: "none", membership: null, attention: false };
}

function renewalForm(membership, paymentContext = EMPTY_PAYMENT_CONTEXT) {
  const currency = membership?.currency || "ARS";
  const canRecordPayment = paymentContext.systemActive ? paymentContext.canRecord : true;
  const register = paymentContext.mode === "erp" ? firstUsableRegister(paymentContext, currency) : null;
  return {
    itemId: membership?.item?.id || "",
    startDate: renewalStart(membership),
    periodValue: membership?.period?.value > 0 ? membership.period.value : 1,
    periodUnit: membership?.period?.unit === "custom" ? "months" : membership?.period?.unit || "months",
    endDate: "", value: String(membership?.value ?? 0),
    currency, renewFromMembershipId: membership?.id || "",
    operationKey: createOperationKey(), recordPayment: canRecordPayment,
    cashRegisterId: register?.id || "", paymentMethod: "cash", paymentReference: "",
  };
}

export default function CustomerMembershipsCard({ projectId, customerId, enabled, canManage, renewMembershipId = "", onMembershipChanged }) {
  const [items, setItems] = React.useState([]);
  const [memberships, setMemberships] = React.useState([]);
  const [paymentConfig, setPaymentConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [form, setForm] = React.useState(emptyForm);
  const autoRenewedRef = React.useRef("");

  const load = React.useCallback(async () => {
    if (!enabled || !projectId || !customerId) return;
    setLoading(true); setError("");
    try {
      const [itemsData, membershipData] = await Promise.all([
        customersApi.membershipItems(projectId),
        customersApi.memberships(projectId, customerId),
      ]);
      setItems(itemsData.items || []);
      setPaymentConfig(itemsData.payment || EMPTY_PAYMENT_CONTEXT);
      setMemberships(membershipData.memberships || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las membresías.");
    } finally {
      setLoading(false);
    }
  }, [customerId, enabled, projectId]);

  React.useEffect(() => {
    setForm(emptyForm()); setItems([]); setMemberships([]); setPaymentConfig(null); setMessage(""); setError("");
    autoRenewedRef.current = "";
    load();
  }, [load]);

  React.useEffect(() => {
    if (!canManage || !paymentConfig || !renewMembershipId || !memberships.length || autoRenewedRef.current === renewMembershipId) return;
    const target = memberships.find((membership) => membership.id === renewMembershipId);
    if (!target) return;
    autoRenewedRef.current = renewMembershipId;
    setForm(renewalForm(target, paymentConfig));
    setMessage("");
    setError("");
  }, [canManage, memberships, paymentConfig, renewMembershipId]);

  if (!enabled) return null;

  const resolvedPaymentConfig = paymentConfig || EMPTY_PAYMENT_CONTEXT;
  const selectedRegister = (resolvedPaymentConfig.registers || []).find((register) => register.id === form.cashRegisterId) || null;
  const paymentReady = !form.recordPayment
    || (resolvedPaymentConfig.mode === "administrative" && resolvedPaymentConfig.canRecord)
    || (
      resolvedPaymentConfig.mode === "erp"
      && resolvedPaymentConfig.canRecord
      && selectedRegister?.available
      && selectedRegister.currency === form.currency
    );

  const change = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const selectItem = (event) => {
    const itemId = event.target.value;
    const item = items.find((candidate) => candidate.id === itemId);
    setForm((current) => ({
      ...current, itemId,
      value: current.renewFromMembershipId ? current.value : String(item?.price ?? 0),
    }));
  };

  const beginRenewal = (membership) => {
    setMessage(""); setError("");
    setForm(renewalForm(membership, resolvedPaymentConfig));
  };

  const togglePayment = (event) => {
    const checked = event.target.checked;
    setForm((current) => {
      if (!checked) return { ...current, recordPayment: false };
      if (resolvedPaymentConfig.mode !== "erp") return { ...current, recordPayment: true };
      const register = firstUsableRegister(resolvedPaymentConfig, current.currency);
      return { ...current, recordPayment: true, cashRegisterId: current.cashRegisterId || register?.id || "" };
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canManage || !form.itemId || !form.startDate || saving || !paymentReady) return;
    setSaving(true); setError(""); setMessage("");
    try {
      const data = await customersApi.assignMembership(projectId, customerId, {
        itemId: form.itemId,
        startDate: form.startDate,
        endDate: form.endDate || undefined,
        periodValue: Number(form.periodValue || 0),
        periodUnit: form.periodUnit,
        value: form.value === "" ? undefined : Number(form.value),
        currency: form.currency || "ARS",
        renewFromMembershipId: form.renewFromMembershipId || undefined,
        operationKey: form.operationKey,
        recordPayment: form.recordPayment,
        cashRegisterId: form.recordPayment && resolvedPaymentConfig.mode === "erp" ? form.cashRegisterId || undefined : undefined,
        paymentMethod: form.recordPayment && resolvedPaymentConfig.mode === "administrative" ? form.paymentMethod : undefined,
        paymentReference: form.recordPayment ? form.paymentReference || undefined : undefined,
      });
      setMessage(data.message || "Membresía guardada.");
      setForm(emptyForm());
      await load();
      if (onMembershipChanged) await onMembershipChanged();
    } catch (err) {
      setError(err.message || "No se pudo guardar la membresía.");
    } finally {
      setSaving(false);
    }
  };

  const overview = membershipOverview(memberships);
  const overviewMembership = overview.membership;
  const overviewPaymentPending = Boolean(
    overviewMembership
    && Number(overviewMembership.value || 0) > 0
    && overviewMembership.payment?.recorded !== true,
  );
  const overviewLabels = {
    active: "Vigente", expiring: "Próxima a vencer", expired: "Vencida", future: "Futura", none: "Sin membresía",
  };
  const availableRegisters = (resolvedPaymentConfig.registers || []).filter(
    (register) => register.available && register.currency === form.currency,
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(25,36,47,.09)" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.4 }}>
        <CardMembershipRoundedIcon sx={{ color: BRAND.orange }} />
        <Box>
          <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Membresías</Typography>
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)" }}>
            Asigná o renová períodos sobre ítems reales. Las renovaciones conservan el historial.
          </Typography>
        </Box>
      </Stack>

      {!loading && overviewMembership ? (
        overview.attention ? (
          <Alert severity={overview.status === "expired" ? "error" : "warning"} sx={{ mb: 1.2 }}
            action={canManage ? <Button size="small" onClick={() => beginRenewal(overviewMembership)}>Renovar</Button> : null}>
            <strong>{overviewLabels[overview.status]}.</strong>{" "}
            {overview.status === "expired" ? "Venció" : "Vence"} el {formatDateOnly(overviewMembership.endDate)} · {overviewMembership.item?.name || "Membresía"}.
          </Alert>
        ) : (
          <Box sx={{ mb: 1.2, p: 1.2, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
              <Box>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)", fontWeight: 850 }}>Estado de membresía</Typography>
                <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                  {overviewLabels[overview.status]} · {overviewMembership.item?.name || "Membresía"}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
                  {overview.status === "future" ? "Comienza" : "Vence"} {overview.status === "future" ? formatDateOnly(overviewMembership.startDate) : formatDateOnly(overviewMembership.endDate)}
                </Typography>
              </Box>
              <Chip size="small" variant="outlined" label={overviewLabels[overview.status]} sx={{ fontWeight: 800 }} />
            </Stack>
          </Box>
        )
      ) : null}

      {!loading && overviewPaymentPending ? (
        <Alert severity="warning" sx={{ mb: 1.2 }}>
          <strong>Cobro pendiente.</strong>{" "}
          Esta membresía tiene {formatMoney(overviewMembership.value, overviewMembership.currency)} sin un cobro registrado.
        </Alert>
      ) : null}

      {error ? <Alert severity="error" sx={{ mb: 1.2 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" sx={{ mb: 1.2 }}>{message}</Alert> : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={28} /></Stack>
      ) : (
        <>
          {canManage ? (
            <Box component="form" onSubmit={submit} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "rgba(25,36,47,.025)" }}>
              {form.renewFromMembershipId ? (
                <Alert severity="info" sx={{ mb: 1.2 }} action={<Button size="small" onClick={() => setForm(emptyForm())}>Cancelar</Button>}>
                  Se creará un período nuevo sin tocar el anterior.
                </Alert>
              ) : null}
              <Grid container spacing={1.2}>
                <Grid item xs={12} md={7}>
                  <TextField select required fullWidth size="small" label="Servicio o plan" value={form.itemId} onChange={selectItem}>
                    {items.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}{item.itemKind === "service" ? " · servicio" : " · ítem"}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField required fullWidth size="small" type="number" label="Valor" value={form.value}
                    onChange={change("value")} inputProps={{ min: 0, step: "0.01" }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField required fullWidth size="small" type="date" label="Inicio" value={form.startDate}
                    onChange={change("startDate")} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <TextField required fullWidth size="small" type="number" label="Período" value={form.periodValue}
                    onChange={change("periodValue")} inputProps={{ min: 1, step: 1 }} disabled={Boolean(form.endDate)} />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <TextField select fullWidth size="small" label="Unidad" value={form.periodUnit}
                    onChange={change("periodUnit")} disabled={Boolean(form.endDate)}>
                    <MenuItem value="days">Días</MenuItem><MenuItem value="weeks">Semanas</MenuItem>
                    <MenuItem value="months">Meses</MenuItem><MenuItem value="years">Años</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" type="date" label="Vencimiento exacto (opcional)" value={form.endDate}
                    onChange={change("endDate")} InputLabelProps={{ shrink: true }} helperText="Reemplaza el período." />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: .2 }} />
                  <FormControlLabel
                    control={<Checkbox checked={form.recordPayment} onChange={togglePayment} disabled={resolvedPaymentConfig.systemActive && !resolvedPaymentConfig.canRecord} />}
                    label={form.renewFromMembershipId ? "Registrar el cobro junto con la renovación" : "Registrar el cobro de esta membresía"}
                  />
                  {resolvedPaymentConfig.note ? (
                    <Typography variant="caption" display="block" sx={{ color: "rgba(25,36,47,.55)", mt: -.5 }}>
                      {resolvedPaymentConfig.note}
                    </Typography>
                  ) : null}
                </Grid>

                {form.recordPayment && resolvedPaymentConfig.mode === "erp" ? (
                  <>
                    <Grid item xs={12} md={7}>
                      <TextField
                        select required fullWidth size="small" label="Caja o cuenta" value={form.cashRegisterId}
                        onChange={change("cashRegisterId")} helperText={!availableRegisters.length ? "No hay una caja o cuenta disponible para esta moneda." : "El método de cobro se toma del tipo de caja o cuenta."}
                      >
                        {(resolvedPaymentConfig.registers || []).map((register) => {
                          const currencyMismatch = register.currency !== form.currency;
                          const disabled = !register.available || currencyMismatch;
                          const reason = currencyMismatch ? `Moneda ${register.currency}` : register.unavailableReason;
                          return (
                            <MenuItem key={register.id} value={register.id} disabled={disabled}>
                              {register.name} · {PAYMENT_METHOD_LABELS[register.method] || register.method}{reason ? ` · ${reason}` : ""}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField fullWidth size="small" label="Referencia del cobro (opcional)" value={form.paymentReference}
                        onChange={change("paymentReference")} inputProps={{ maxLength: 180 }} />
                    </Grid>
                    {!resolvedPaymentConfig.canRecord ? (
                      <Grid item xs={12}><Alert severity="warning">Podés renovar sin cobro, pero tus permisos actuales no permiten registrar la cobranza en Gestión del negocio.</Alert></Grid>
                    ) : !availableRegisters.length ? (
                      <Grid item xs={12}><Alert severity="warning">Abrí o configurá una caja/cuenta compatible antes de registrar el cobro. También podés desmarcar el cobro y renovar solamente el período.</Alert></Grid>
                    ) : null}
                  </>
                ) : null}

                {form.recordPayment && resolvedPaymentConfig.mode === "administrative" ? (
                  <>
                    <Grid item xs={12}><Alert severity="info">Este registro es administrativo: deja constancia del pago en la membresía y no genera caja, movimiento ni asiento contable.</Alert></Grid>
                    <Grid item xs={12} md={5}>
                      <TextField select fullWidth size="small" label="Medio de pago" value={form.paymentMethod} onChange={change("paymentMethod")}>
                        {(resolvedPaymentConfig.methods || Object.keys(PAYMENT_METHOD_LABELS)).map((method) => (
                          <MenuItem key={method} value={method}>{PAYMENT_METHOD_LABELS[method] || method}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <TextField fullWidth size="small" label="Referencia del cobro (opcional)" value={form.paymentReference}
                        onChange={change("paymentReference")} inputProps={{ maxLength: 180 }} />
                    </Grid>
                  </>
                ) : null}
              </Grid>
              <Button type="submit" variant="contained" sx={{ mt: 1.2 }} disabled={saving || !items.length || !form.itemId || !paymentReady}
                startIcon={saving ? <CircularProgress size={17} /> : <CardMembershipRoundedIcon />}>
                {form.renewFromMembershipId ? "Confirmar renovación" : "Asignar membresía"}
              </Button>
              {!items.length ? <Typography variant="caption" sx={{ ml: 1.2, color: "warning.dark", fontWeight: 800 }}>Creá primero un ítem activo.</Typography> : null}
            </Box>
          ) : null}

          <Divider sx={{ my: 1.6 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 950, color: BRAND.dark, mb: .8 }}>Historial de períodos</Typography>
          {!memberships.length ? (
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)" }}>Todavía no tiene membresías.</Typography>
          ) : (
            <Stack spacing={.9}>
              {memberships.map((membership) => {
                const meta = statusMeta(membership.status);
                const payMeta = paymentMeta(membership.payment);
                const periodLabel = membership.period?.unit === "custom"
                  ? "vencimiento manual"
                  : `${membership.period?.value || 0} ${PERIOD_LABELS[membership.period?.unit] || membership.period?.unit}`;
                const paymentDetails = membership.payment?.recorded
                  ? [
                      PAYMENT_METHOD_LABELS[membership.payment.method] || membership.payment.method,
                      membership.payment.cashRegister?.name,
                      membership.payment.erp?.movementNumber ? `Mov. ${membership.payment.erp.movementNumber}` : "",
                      membership.payment.reference ? `Ref. ${membership.payment.reference}` : "",
                      formatDateTime(membership.payment.paidAt),
                    ].filter(Boolean).join(" · ")
                  : "";
                return (
                  <Box key={membership.id} sx={{ p: 1.2, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
                      <Box>
                        <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap">
                          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{membership.item?.name || "Membresía"}</Typography>
                          <Chip size="small" color={meta.color} label={meta.label} sx={{ fontWeight: 800 }} />
                          {membership.renewedFromMembershipId ? <Chip size="small" variant="outlined" label="Renovación" /> : null}
                          {payMeta ? <Chip size="small" color={payMeta.color} variant="outlined" label={payMeta.label} /> : null}
                        </Stack>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
                          {formatDateOnly(membership.startDate)} → {formatDateOnly(membership.endDate)} · {periodLabel} · {formatMoney(membership.value, membership.currency)}
                        </Typography>
                        {paymentDetails ? (
                          <Typography variant="caption" display="block" sx={{ color: "rgba(25,36,47,.52)" }}>
                            Cobro: {formatMoney(membership.payment.amount, membership.payment.currency)} · {paymentDetails}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.48)" }}>
                          Cargó {membership.assignedBy?.name || membership.assignedBy?.email || "Sistema"}
                        </Typography>
                      </Box>
                      {canManage && membership.status !== "scheduled" ? (
                        <Button size="small" variant="outlined" startIcon={<AutorenewRoundedIcon />} onClick={() => beginRenewal(membership)}>Renovar</Button>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Paper>
  );
}
